import { useEffect, useState } from 'react';
import { useOnboardingFlow } from '@/state/onboarding/useOnboardingFlow';
import { trackUmami } from '@/lib/umami/umami';
import { accountTypeOf } from '@/lib/umami/umami-events';
import { FIRST_MESSAGE_DELAY } from '../../Authentication/chat/chat-pacing';
import { useChat } from '../../Authentication/chat/use-chat';
import { mainScriptTag } from '../../Dashboard/Websites/List/ScriptsModal';
import { SNIPPET_TIPS, WEBSITE_TIPS } from './connect-script';
import { parseWebsiteAddress } from '@/state/onboarding/website-address';

export type InstallMethod = 'paste' | 'gtm';

/**
 * The connect-your-website page: the address form, the snippet that unlocks once a
 * website is saved, and the tips conversation beside them. All of its state lives here.
 *
 * Saving goes through useOnboardingFlow, so the first save creates the website and any
 * later one changes the same website's address. Nothing refetches onboarding until
 * `finish`: see useOnboarding for why.
 *
 * The snippet tips wait for the website tips to finish rather than interleave with them,
 * and play once.
 */
type UseConnectWebsiteParams = { isAgency: boolean };

export const useConnectWebsite = ({ isAgency }: UseConnectWebsiteParams) => {
  const flow = useOnboardingFlow({ isAgency });
  const type = accountTypeOf(isAgency);
  const chat = useChat();
  const { say, begin, busy } = chat;

  const [url, setUrl] = useState('');
  const [touched, setTouched] = useState(false);
  const [isEditing, setIsEditing] = useState(true);
  const [method, setMethod] = useState<InstallMethod>('paste');
  const [isCopied, setIsCopied] = useState(false);
  const [snippetTipsDue, setSnippetTipsDue] = useState(false);
  const [isFinishing, setIsFinishing] = useState(false);

  const address = parseWebsiteAddress(url);
  const website = flow.createdWebsite;
  const hasSite = !!website && !isEditing;
  const snippet = website ? mainScriptTag(website.id) : '';
  const savedAddress = website ? parseWebsiteAddress(website.url) : null;

  useEffect(() => {
    begin({ delay: FIRST_MESSAGE_DELAY, then: () => say(WEBSITE_TIPS) });
    // Once, when the page opens.
  }, []);

  useEffect(() => {
    if (!snippetTipsDue || busy) return;
    setSnippetTipsDue(false);
    say(SNIPPET_TIPS);
  }, [snippetTipsDue, busy]);

  const submit = async () => {
    setTouched(true);
    if (!address || flow.isSaving) return;
    const isFirstSave = !website;
    const saved = await flow.saveWebsite(address.href);
    if (!saved) return;
    // Only the first save: a later one changes the same website's address.
    if (isFirstSave) trackUmami('website-added', { type });
    setIsEditing(false);
    if (isFirstSave) setSnippetTipsDue(true);
  };

  const edit = () => {
    setUrl(savedAddress?.href ?? website?.url ?? url);
    setTouched(false);
    setIsEditing(true);
  };

  const copy = async () => {
    await navigator.clipboard.writeText(snippet);
    trackUmami('snippet-copied', { type, method });
    setIsCopied(true);
    setTimeout(() => setIsCopied(false), 1800);
  };

  return {
    chat,
    url,
    setUrl: (value: string) => {
      setUrl(value);
      setTouched(true);
    },
    isInvalid: touched && !!url.trim() && !address,
    canSubmit: !!address && !flow.isSaving,
    isSaving: flow.isSaving,
    submit,
    isEditing,
    hasSite,
    domain: savedAddress?.host ?? '',
    savedHref: savedAddress?.href ?? website?.url ?? '',
    edit,
    method,
    setMethod,
    snippet,
    isCopied,
    copy,
    // Guarded: finish waits on several refetches, and a second click in that time would
    // run it, and count the step, twice.
    finish: async () => {
      if (isFinishing) return;
      setIsFinishing(true);
      trackUmami('onboarding-completed', { type });
      try {
        await flow.finish();
      } finally {
        setIsFinishing(false);
      }
    },
  };
};

export type ConnectWebsiteManager = ReturnType<typeof useConnectWebsite>;
